import { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Maximize2, Network } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type {
  KnowledgeGraphEdgeViewModel,
  KnowledgeGraphNodeViewModel,
} from '../../types/learningAnalysis';
import { createKnowledgeGraphEdges } from '../../utils/knowledgeGraphLayout';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';
import { KnowledgeGraphCanvas } from './KnowledgeGraphCanvas';
import { KnowledgeGraphDetailSheet } from './KnowledgeGraphDetailSheet';

type KnowledgeGraphPreviewProps = {
  nodes: KnowledgeGraphNodeViewModel[];
  edges?: KnowledgeGraphEdgeViewModel[];
  onOpenFullscreen: () => void;
  onStartPractice?: (node: KnowledgeGraphNodeViewModel) => void;
};

export function KnowledgeGraphPreview({
  nodes,
  edges,
  onOpenFullscreen,
  onStartPractice,
}: KnowledgeGraphPreviewProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(nodes[0]?.id ?? null);
  const graphEdges = useMemo(() => edges ?? createKnowledgeGraphEdges(nodes), [edges, nodes]);
  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? nodes[0] ?? null;

  return (
    <AppCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Network size={18} color={tokens.colors.primary} />
          <View style={styles.titleCopy}>
            <AppText variant="body" weight="bold">
              知识图谱
            </AppText>
            <AppText variant="caption" tone="muted">
              点击节点查看薄弱原因和练习入口
            </AppText>
          </View>
        </View>
        <AppButton
          label="全屏"
          variant="secondary"
          onPress={onOpenFullscreen}
          leftSlot={<Maximize2 size={16} color={tokens.colors.primary} />}
          style={styles.fullscreenButton}
        />
      </View>

      <KnowledgeGraphCanvas
        nodes={nodes}
        edges={graphEdges}
        selectedNodeId={selectedNodeId}
        onSelectNode={(node) => setSelectedNodeId(node.id)}
        compact
        height={218}
      />

      <KnowledgeGraphDetailSheet
        node={selectedNode}
        onClose={() => setSelectedNodeId(null)}
        onAction={onStartPractice}
      />

      <View style={styles.legendRow}>
        <AppText variant="caption" tone="muted" weight="bold">
          图例
        </AppText>
        <LegendDot label="已掌握" color={tokens.colors.success} />
        <LegendDot label="待复习" color={tokens.colors.warning} />
        <LegendDot label="薄弱" color={tokens.colors.danger} />
      </View>
    </AppCard>
  );
}

function LegendDot({ label, color }: { label: string; color: string }) {
  return (
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: color }]} />
      <AppText variant="caption" tone="muted">
        {label}
      </AppText>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  titleCopy: {
    flex: 1,
  },
  fullscreenButton: {
    minHeight: 34,
    paddingHorizontal: tokens.spacing.md,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
